import type { Point, MeasurementMode } from './types.js';

/**
 * Measures the distance between two grid points in feet.
 *
 * @param from      - start point (in grid cells, integers)
 * @param to        - end point (in grid cells, integers)
 * @param mode      - calculation mode
 * @param gridSizeFt - feet per grid cell (default 5)
 */
export function measureDistance(
  from: Point,
  to: Point,
  mode: MeasurementMode,
  gridSizeFt: number = 5,
  /** running diagonal count for the 'alternating' mode (mutated in place) */
  diagonalState?: { count: number },
): number {
  const dx = Math.abs(to.x - from.x);
  const dy = Math.abs(to.y - from.y);

  switch (mode) {
    case 'simple': {
      // D&D 5e PHB: diagonal = 1 cell = gridSizeFt
      const straight = Math.max(dx, dy);
      return straight * gridSizeFt;
    }

    case 'alternating': {
      // D&D 5e DMG variant: diagonals alternate 5 / 10 / 5 / 10 ft
      const straight = Math.abs(dx - dy);
      const diags = Math.min(dx, dy);
      const state = diagonalState ?? { count: 0 };
      let diagCost = 0;
      for (let i = 0; i < diags; i++) {
        // odd diagonal (1st, 3rd, …) costs 1 cell; even (2nd, 4th, …) costs 2 cells
        const isEven = (state.count + 1) % 2 === 0;
        diagCost += isEven ? 2 : 1;
        state.count += 1;
      }
      return (straight + diagCost) * gridSizeFt;
    }

    case 'euclidean': {
      // Pythagorean distance
      return Math.sqrt(dx * dx + dy * dy) * gridSizeFt;
    }

    case 'hex': {
      // Offset hex grid distance (cube coordinate formula reduced to 2D offset)
      // Works for flat-top hexes with offset rows.
      // For simplicity we use the axial cube distance.
      const q1 = from.x;
      const r1 = from.y;
      const q2 = to.x;
      const r2 = to.y;
      const dq = Math.abs(q2 - q1);
      const dr = Math.abs(r2 - r1);
      const hexDist = Math.max(dq, dr, Math.abs((q2 + r2) - (q1 + r1)));
      return hexDist * gridSizeFt;
    }
  }
}

export interface SegmentMeasurement {
  from: Point;
  to: Point;
  distance: number;
}

export interface PathMeasurement {
  segments: SegmentMeasurement[];
  total: number;
}

/**
 * Measures a polyline path through multiple waypoints.
 * Returns per-segment distances and total distance.
 */
export function measurePath(
  points: Point[],
  mode: MeasurementMode,
  gridSizeFt: number = 5,
): PathMeasurement {
  if (points.length < 2) {
    return { segments: [], total: 0 };
  }

  const segments: SegmentMeasurement[] = [];
  // shared diagonal state for alternating mode (spans entire path)
  const diagonalState = { count: 0 };
  let total = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const distance = measureDistance(points[i], points[i + 1], mode, gridSizeFt, diagonalState);
    segments.push({ from: points[i], to: points[i + 1], distance });
    total += distance;
  }

  return { segments, total };
}
